import { Component, OnInit, signal, inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { MessageService } from 'primeng/api';

import {
  SearchFormComponent,
  SearchFormData,
} from '@shared/components/search-form/search-form.component';
import { DataTableComponent } from '@shared/components/data-table/data-table.component';
import {
  ConfirmationDialogComponent,
  ConfirmationButton,
} from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import {
  ChangeApprovalService,
  ChangeApprovalSearchParams,
} from './change-approval.service';
import {
  ChangeApprovalRecord,
  CHANGE_APPROVAL_SEARCH_CONFIG,
  CHANGE_APPROVAL_TABLE_CONFIG,
  CHANGE_APPROVAL_DEFAULT_CONFIG,
} from './change-approval.config';

@Component({
  selector: 'app-change-approval',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    ToastModule,
    TranslateModule,
    SearchFormComponent,
    DataTableComponent,
    ConfirmationDialogComponent,
  ],
  providers: [MessageService],

  templateUrl: './change-approval.component.html',
  styleUrl: './change-approval.component.css',
})
export class ChangeApprovalComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly changeApprovalService = inject(ChangeApprovalService);
  private readonly messageService = inject(MessageService);
  private readonly translateService = inject(TranslateService);
  private readonly destroy$ = new Subject<void>();

  // Tab management
  activeTab = signal<'virtual' | 'inventory' | 'sub-warehouse'>('virtual');

  // Search form configuration
  searchFormat = signal(CHANGE_APPROVAL_SEARCH_CONFIG);

  // Table configuration
  columns = CHANGE_APPROVAL_TABLE_CONFIG;

  // Data signals
  dataSource = signal<ChangeApprovalRecord[]>([]);
  loading = signal(false);
  totalRecords = signal(0);
  first = signal(0);
  rows = signal(CHANGE_APPROVAL_DEFAULT_CONFIG.PAGE_SIZE);

  // Confirmation dialog properties
  showConfirmationDialog = false;
  selectedRecord: ChangeApprovalRecord | null = null;
  dialogTitle = '';
  dialogMessage = '';
  dialogButtons: ConfirmationButton[] = [];
  currentAction: 'approve' | 'reject' | 'delete' | null = null;

  ngOnInit(): void {
    this.loadData();
    this.loadOptions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearch(searchData: SearchFormData): void {
    this.first.set(0);
    this.loadData(searchData);
  }

  onReset(): void {
    this.first.set(0);
    this.loadData();
  }

  onPageChange(event: any): void {
    this.first.set(event.first);
    this.rows.set(event.rows);
    this.loadData();
  }

  onTabChange(tab: 'virtual' | 'inventory' | 'sub-warehouse'): void {
    this.activeTab.set(tab);
    this.first.set(0);
    this.loadData();
  }

  onViewDetails(record: ChangeApprovalRecord): void {
    this.router.navigate(['/transfers/details', record.id]);
  }

  onAllocation(record: ChangeApprovalRecord): void {
    this.router.navigate(['/transfers/allocation', record.id]);
  }

  onApprove(record: ChangeApprovalRecord): void {
    this.openConfirmDialog(record, 'approve', 'success');
  }

  onReject(record: ChangeApprovalRecord): void {
    this.openConfirmDialog(record, 'reject', 'secondary');
  }

  onDelete(record: ChangeApprovalRecord): void {
    this.openConfirmDialog(record, 'delete', 'danger');
  }

  private openConfirmDialog(
    record: ChangeApprovalRecord,
    action: 'approve' | 'reject' | 'delete',
    confirmSeverity: 'success' | 'secondary' | 'danger',
  ): void {
    this.selectedRecord = record;
    this.currentAction = action;
    this.dialogTitle = this.translateService.instant(
      `inventory_change_approval.dialogs.${action}.title`,
    );
    this.dialogMessage = this.translateService.instant(
      `inventory_change_approval.dialogs.${action}.message`,
      { orderNumber: record.transferOrderNumber },
    );
    this.dialogButtons = [
      {
        label: this.translateService.instant('common.actions.cancel'),
        action: 'cancel',
        outlined: true,
      },
      {
        label: this.translateService.instant(`common.actions.${action}`),
        action: 'confirm',
        severity: confirmSeverity,
      },
    ];
    this.showConfirmationDialog = true;
  }

  private loadData(searchParams?: SearchFormData): void {
    this.loading.set(true);

    const params: ChangeApprovalSearchParams = {
      page: Math.floor(this.first() / this.rows()),
      size: this.rows(),
      ...searchParams,
    };

    this.changeApprovalService
      .getChangeApprovalRecords(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.dataSource.set(response.content);
          this.totalRecords.set(response.totalElements);
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
        },
      });
  }

  // Load options with translations
  private loadOptions(): void {
    // Translate status options
    const statusField = this.searchFormat().find(
      (field) => field.key === 'status',
    );
    if (statusField && statusField.options) {
      const translatedOptions = statusField.options.map((option) => ({
        ...option,
        label: this.translateService.instant(option.label),
      }));

      this.searchFormat.set(
        this.searchFormat().map((field) =>
          field.key === 'status'
            ? { ...field, options: translatedOptions }
            : field,
        ),
      );
    }
  }

  // Status methods for transfer status
  getStatusClass(status: string): string {
    return `status-badge status-${status}`;
  }

  formatTransferStatus(status: string): string {
    const statusMap: { [key: string]: string } = {
      wait: 'inventory_change_approval.status.pending',
      'wait-execute': 'inventory_change_approval.status.pending_execution',
      executed: 'inventory_change_approval.status.executed',
      'execute-fail': 'inventory_change_approval.status.executed_failed',
      cancel: 'inventory_change_approval.status.cancelled',
      fail: 'inventory_change_approval.status.failed',
    };

    return this.translateService.instant(statusMap[status] || status);
  }

  getStatusSeverity(status: string): string {
    switch (status) {
      case 'wait':
        return 'warning';
      case 'wait-execute':
        return 'info';
      case 'executed':
        return 'success';
      case 'execute-fail':
      case 'fail':
        return 'danger';
      case 'cancel':
        return 'secondary';
      default:
        return 'info';
    }
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  }

  // Confirmation dialog methods
  onConfirmatiButtonClick(action: string): void {
    if (action === 'cancel') {
      this.closeConfirmationDialog();
    } else if (action === 'confirm') {
      if (this.currentAction === 'approve') {
        this.confirmApprove();
      } else if (this.currentAction === 'reject') {
        this.confirmReject();
      } else if (this.currentAction === 'delete') {
        this.confirmDelete();
      }
    }
  }

  confirmApprove(): void {
    this.executeConfirmedAction(
      this.changeApprovalService.approveOrder(this.selectedRecord!.id),
      'approve',
    );
  }

  confirmReject(): void {
    this.executeConfirmedAction(
      this.changeApprovalService.rejectOrder(this.selectedRecord!.id, ''),
      'reject',
    );
  }

  confirmDelete(): void {
    this.executeConfirmedAction(
      this.changeApprovalService.deleteChangeApprovalRecord(
        this.selectedRecord!.id,
      ),
      'delete',
    );
  }

  private executeConfirmedAction(
    action$: ReturnType<typeof this.changeApprovalService.approveOrder>,
    action: 'approve' | 'reject' | 'delete',
  ): void {
    if (this.selectedRecord) {
      action$.pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              `inventory_change_approval.messages.${action}_success`,
            ),
          });
          this.loadData();
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              `inventory_change_approval.messages.${action}_error`,
            ),
          });
        },
      });
    }
    this.closeConfirmationDialog();
  }

  closeConfirmationDialog(): void {
    this.showConfirmationDialog = false;
    this.selectedRecord = null;
    this.currentAction = null;
    this.dialogTitle = '';
    this.dialogMessage = '';
    this.dialogButtons = [];
  }
}
